import { useMemo, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Menu, LogOut, ChevronRight, X, Command, Search, Settings2, Bell } from "lucide-react";
import { trpc, queryClient } from "../../lib/trpc";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import { fmtCurrency } from "../../lib/format";
import { NAV_BY_ROLE, type NavItem } from "./nav-config";
import { useNotifications } from "../../contexts/NotificationContext";
import { NotificationToaster } from "../notifications/NotificationToaster";
import { SUPPORTED_LANGUAGES, type Language } from "../../../../shared/const";
import { Wordmark, LogoImage } from "../Logo";
import { ThemeToggle } from "../theme-toggle";

const ROLE_LABEL: Record<string, Record<Language, string>> = {
  admin: { "zh-TW": "管理員", "en-US": "Admin", "zh-CN": "管理员" },
  dispatcher: { "zh-TW": "調度員", "en-US": "Dispatcher", "zh-CN": "调度员" },
  agency: { "zh-TW": "需求方", "en-US": "Agency", "zh-CN": "需求方" },
  fleet: { "zh-TW": "供應方", "en-US": "Fleet", "zh-CN": "供应方" },
};

const ROLE_BADGE: Record<string, "primary" | "info" | "warning" | "success"> = {
  admin: "warning",
  dispatcher: "info",
  agency: "primary",
  fleet: "success",
};

/** /account 不在側欄導覽內，麵包屑用此項 */
const ACCOUNT_NAV_ITEM: NavItem = {
  to: "/account",
  icon: Settings2,
  label: { "zh-TW": "帳號設定", "en-US": "Account settings", "zh-CN": "帐号设定" },
};

const LANG_LABEL: Record<Language, string> = {
  "zh-TW": "繁",
  "en-US": "EN",
  "zh-CN": "简",
};

/** D2：需求方側欄信用額度（戶口/已用/可用，一律 HKD；未設限則不顯示）。 */
function AgencyCreditPanel() {
  const { t } = useLanguage();
  const { data: c } = trpc.order.myCredit.useQuery(undefined, { staleTime: 30_000 });
  if (!c || c.limit == null) return null;
  const over = c.available != null && c.available < 0;
  const rows: Array<{ label: string; value: number; emphasize?: boolean }> = [
    { label: t({ "zh-TW": "戶口額度", "en-US": "Credit limit", "zh-CN": "户口额度" }), value: c.limit },
    { label: t({ "zh-TW": "已使用額度", "en-US": "Used", "zh-CN": "已使用额度" }), value: c.used },
    { label: t({ "zh-TW": "可用額度", "en-US": "Available", "zh-CN": "可用额度" }), value: c.available ?? 0, emphasize: true },
  ];
  return (
    <div className="border-t border-sidebar-border px-4 py-3">
      <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70">
        {t({ "zh-TW": "信用額度", "en-US": "Credit", "zh-CN": "信用额度" })}
      </p>
      <dl className="space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-2 text-[12px]">
            <dt className="text-muted-foreground">{r.label}</dt>
            <dd
              className={cn(
                "numeric font-mono tabular-nums",
                r.emphasize && (over ? "font-semibold text-destructive" : "font-semibold text-foreground"),
              )}
            >
              {fmtCurrency(r.value, "HKD")}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, refetch } = useAuth();
  const { t, lang, setLang } = useLanguage();
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = trpc.auth.logout.useMutation({
    onSuccess: () => {
      queryClient.clear();
      refetch();
    },
  });

  // 站內提醒（2026-08-27）：未讀徽章
  const { unreadCount } = useNotifications();

  // 帳號管理：頂欄語言切換同步寫回使用者設定（登入頁未登入時不會進到這裡）
  const updateLanguage = trpc.auth.updateLanguage.useMutation();
  const switchLang = (l: Language) => {
    setLang(l);
    updateLanguage.mutate({ language: l });
  };

  const currentItem: NavItem | null = useMemo(() => {
    if (!user) return null;
    if (location.startsWith("/account")) return ACCOUNT_NAV_ITEM;
    return (
      NAV_BY_ROLE[user.role]
        .flatMap((g) => g.items)
        .find((i) => i.to === location || (i.to !== "/" && location.startsWith(i.to))) ?? null
    );
  }, [user, location]);

  if (!user) return null;
  const groups = NAV_BY_ROLE[user.role];

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      {/* mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-foreground/30 backdrop-blur-sm md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Sidebar ───────────────────────────────────────── */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-[260px] flex-col border-r border-sidebar-border bg-sidebar transition-transform md:relative md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        )}
      >
        {/* brand — 可點擊回工作台（與「工作台」選單同行為） */}
        <div className="flex h-[60px] items-center justify-between border-b border-sidebar-border px-4">
          <Link
            href="/"
            onClick={() => setMobileOpen(false)}
            className="rounded-md transition-opacity hover:opacity-75"
            aria-label={t({ "zh-TW": "回工作台", "en-US": "Back to workspace", "zh-CN": "回工作台" })}
          >
            <Wordmark role={user.role} />
          </Link>
          <button
            className="md:hidden text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setMobileOpen(false)}
            aria-label="close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* fake search (visual placeholder, no logic yet) */}
        <div className="px-4 pt-3">
          <button className="group flex w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:border-border-strong">
            <Search className="h-3.5 w-3.5" strokeWidth={2} />
            <span className="flex-1">
              {t({ "zh-TW": "搜尋訂單、合作方…", "en-US": "Search orders, parties…", "zh-CN": "搜寻订单、合作方…" })}
            </span>
            <kbd className="font-mono text-[12px] leading-none opacity-60 group-hover:opacity-100">
              <Command className="inline h-3 w-3" />K
            </kbd>
          </button>
        </div>

        {/* nav groups */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {groups.map((group, gi) => (
            <div key={gi} className="mb-5">
              <p className="px-2 pb-1.5 font-mono text-[12px] uppercase tracking-[0.12em] text-muted-foreground/70">
                {t(group.groupLabel)}
              </p>
              <ul className="space-y-px">
                {group.items.map((item) => {
                  const isActive =
                    location === item.to || (item.to !== "/" && location.startsWith(item.to));
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <Link
                        href={item.to}
                        onClick={() => setMobileOpen(false)}
                        className={cn(
                          "group relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors",
                          isActive
                            ? "bg-sidebar-active-bg font-medium text-sidebar-active-fg"
                            : "text-foreground/75 hover:bg-muted hover:text-foreground",
                        )}
                      >
                        {/* active rail */}
                        <span
                          className={cn(
                            "absolute left-[-12px] top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full transition-all",
                            isActive ? "bg-primary opacity-100" : "opacity-0",
                          )}
                          aria-hidden
                        />
                        <Icon
                          className={cn(
                            "h-4 w-4 shrink-0 transition-colors",
                            isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
                          )}
                          strokeWidth={isActive ? 2.25 : 1.75}
                        />
                        <span className="truncate">{t(item.label)}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {user.role === "agency" && <AgencyCreditPanel />}

        {/* footer: user card */}
        <div className="border-t border-sidebar-border p-3">
          <Link
            href="/account"
            onClick={() => setMobileOpen(false)}
            title={t({ "zh-TW": "帳號設定", "en-US": "Account settings", "zh-CN": "帐号设定" })}
            className={cn(
              "flex items-center gap-2.5 rounded-md px-2 py-2 transition-colors hover:bg-muted",
              location.startsWith("/account") && "bg-sidebar-active-bg",
            )}
          >
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-display text-sm font-semibold text-primary-foreground ${
                user.role === "agency" || user.role === "fleet"
                  ? "bg-gradient-to-br from-warm via-primary to-primary"
                  : "bg-gradient-to-br from-primary to-primary/70"
              }`}
            >
              {user.name.trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium leading-tight">{user.name}</p>
              <p className="truncate text-[12px] text-muted-foreground">{user.email}</p>
            </div>
            <Badge variant={ROLE_BADGE[user.role] ?? "default"} size="sm">
              {t(ROLE_LABEL[user.role]!)}
            </Badge>
            <Settings2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Link>
        </div>
      </aside>

      {/* ── Main ──────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-[60px] items-center gap-3 border-b border-border bg-background/85 px-4 backdrop-blur-md md:px-6">
          <button
            className="md:hidden text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setMobileOpen(true)}
            aria-label="open menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* breadcrumb */}
          <nav className="flex min-w-0 items-center gap-1.5 text-[13px]">
            <span className="text-muted-foreground">{t(ROLE_LABEL[user.role]!)}</span>
            {currentItem && (
              <>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                <span className="truncate font-medium">{t(currentItem.label)}</span>
              </>
            )}
          </nav>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <div className="hidden items-center gap-0.5 rounded-md border border-border bg-background p-0.5 sm:inline-flex">
              {SUPPORTED_LANGUAGES.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => switchLang(l)}
                  className={cn(
                    "h-7 min-w-[28px] rounded-[5px] px-1.5 font-mono text-[12px] transition-colors",
                    lang === l
                      ? "bg-primary-soft text-primary-soft-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                  aria-pressed={lang === l}
                >
                  {LANG_LABEL[l]}
                </button>
              ))}
            </div>
            {/* 站內提醒：未讀徽章，點擊進通知中心 */}
            <Link
              href="/notifications"
              aria-label={
                unreadCount > 0
                  ? t({
                      "zh-TW": `通知中心，${unreadCount} 則未讀`,
                      "en-US": `Notifications, ${unreadCount} unread`,
                      "zh-CN": `通知中心，${unreadCount} 则未读`,
                    })
                  : t({ "zh-TW": "通知中心", "en-US": "Notifications", "zh-CN": "通知中心" })
              }
              title={t({ "zh-TW": "通知中心", "en-US": "Notifications", "zh-CN": "通知中心" })}
              className="relative inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--color-destructive)] px-1 font-mono text-[10px] font-medium leading-none text-destructive-foreground">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </Link>
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => logout.mutate()}>
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden md:inline">
                {t({ "zh-TW": "登出", "en-US": "Sign out", "zh-CN": "登出" })}
              </span>
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-x-auto px-4 py-6 md:px-8 md:py-8">
          <div className="animate-fade-up">{children}</div>
        </main>

        {/* D9：DreamTour 頁尾（品牌 logo ＋ WhatsApp／電話／Email／牌照） */}
        <footer className="border-t border-border px-4 py-4 md:px-8">
          <div className="flex flex-col items-center gap-2 text-center sm:flex-row sm:justify-between sm:text-left">
            <div className="flex items-center gap-2.5">
              <LogoImage className="h-6" />
              <span className="text-[12px] text-muted-foreground">Licence No. 352597</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
              <a href="https://wa.me/85291515959" target="_blank" rel="noreferrer" className="transition-colors hover:text-foreground">
                WhatsApp +852 9151 5959
              </a>
              <span>Tel (852) 2334 3346</span>
              <a href="mailto:ben@dreamtour.com.hk" className="transition-colors hover:text-foreground">
                ben@dreamtour.com.hk
              </a>
            </div>
          </div>
        </footer>
      </div>
      <NotificationToaster />
    </div>
  );
}
